import '../styles/GlobalDesign.css';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';

const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? '/api' 
  : '/api';

const AdminHome = () => {
  const [stats, setStats] = useState(null);
  const [revenueStats, setRevenueStats] = useState(null);
  const [deviceStats, setDeviceStats] = useState(null);
  const [resellerStats, setResellerStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadAllStats();
  }, []);

  const loadAllStats = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [statsRes, revenueRes, devicesRes, resellersRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/admin/stats`, { headers }),
        axios.get(`${API_BASE_URL}/admin/revenue/stats`, { headers }),
        axios.get(`${API_BASE_URL}/admin/devices/stats`, { headers }),
        axios.get(`${API_BASE_URL}/admin/resellers`, { headers }).catch(() => ({ data: { stats: null } }))
      ]);

      setStats(statsRes.data);
      setRevenueStats(revenueRes.data);
      setDeviceStats(devicesRes.data);
      setResellerStats(resellersRes.data.stats);
    } catch (err) {
      console.error('Erreur chargement stats:', err);
      setError('Erreur de chargement des statistiques');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="admin-home">
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Chargement des statistiques...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="admin-home">
          <div className="error-container">
            <p>❌ {error}</p>
            <button onClick={loadAllStats} className="retry-btn">Réessayer</button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-home">
        <div className="dashboard-header">
          <h1>📊 Tableau de Bord</h1>
          <button onClick={loadAllStats} className="refresh-btn" disabled={loading}>
            🔄 Actualiser
          </button>
        </div>

        {/* Statistiques Générales */}
        <section className="stats-section">
          <h2>📈 Statistiques Générales</h2>
          <div className="stats-grid">
            <div className="stat-card primary">
              <div className="stat-icon">👥</div>
              <div className="stat-content">
                <h3>Utilisateurs Total</h3>
                <div className="stat-value">{stats?.totals?.users || 0}</div>
              </div>
            </div>

            <div className="stat-card success">
              <div className="stat-icon">✅</div>
              <div className="stat-content">
                <h3>Abonnements Actifs</h3>
                <div className="stat-value">{stats?.totals?.activeSubscriptions || 0}</div>
              </div>
            </div>

            <div className="stat-card info">
              <div className="stat-icon">📺</div>
              <div className="stat-content">
                <h3>Chaînes Actives</h3>
                <div className="stat-value">{stats?.totals?.channels || 0}</div>
              </div>
            </div>

            <div className="stat-card warning">
              <div className="stat-icon">💰</div>
              <div className="stat-content">
                <h3>Revenu Total</h3>
                <div className="stat-value">{formatCurrency(stats?.totals?.revenue)}</div>
              </div>
            </div>
          </div>
        </section>

        {/* Revenus */}
        <section className="stats-section">
          <h2>💵 Revenus</h2>
          <div className="stats-grid">
            <div className="stat-card revenue-today">
              <div className="stat-icon">📅</div>
              <div className="stat-content">
                <h3>Aujourd'hui</h3>
                <div className="stat-value">{formatCurrency(revenueStats?.today?.revenue)}</div>
                <div className="stat-detail">{revenueStats?.today?.count || 0} paiements</div>
              </div>
            </div>

            <div className="stat-card revenue-month">
              <div className="stat-icon">📆</div>
              <div className="stat-content">
                <h3>Ce Mois</h3>
                <div className="stat-value">{formatCurrency(revenueStats?.month?.revenue)}</div>
                <div className="stat-detail">{revenueStats?.month?.count || 0} paiements</div>
              </div>
            </div>

            <div className="stat-card revenue-total">
              <div className="stat-icon">💎</div>
              <div className="stat-content">
                <h3>Total Général</h3>
                <div className="stat-value">{formatCurrency(revenueStats?.total?.revenue)}</div>
                <div className="stat-detail">{revenueStats?.total?.count || 0} paiements</div>
              </div>
            </div>

            <div className="stat-card revenue-30days">
              <div className="stat-icon">⏱️</div>
              <div className="stat-content">
                <h3>30 Derniers Jours</h3>
                <div className="stat-value">{formatCurrency(stats?.stats?.revenue30Days)}</div>
                <div className="stat-detail">{stats?.stats?.paymentsToday || 0} paiements aujourd'hui</div>
              </div>
            </div>
          </div>
        </section>

        {/* Abonnements par Plan */}
        {stats?.subscriptionsByPlan && stats.subscriptionsByPlan.length > 0 && (
          <section className="stats-section">
            <h2>📋 Abonnements par Plan</h2>
            <div className="plans-grid">
              {stats.subscriptionsByPlan.map((plan, index) => (
                <div key={index} className="plan-card">
                  <div className="plan-name">{plan.plan_type || 'Non défini'}</div>
                  <div className="plan-stats">
                    <div className="plan-count">
                      <span className="label">Abonnés</span>
                      <span className="value">{plan.count || 0}</span>
                    </div>
                    <div className="plan-revenue">
                      <span className="label">Revenu</span>
                      <span className="value">{formatCurrency(plan.revenue)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Revendeurs */}
        {resellerStats && (
          <section className="stats-section">
            <h2>🤝 Statistiques Revendeurs</h2>
            <div className="stats-grid">
              <div className="stat-card reseller-total">
                <div className="stat-icon">👔</div>
                <div className="stat-content">
                  <h3>Total Revendeurs</h3>
                  <div className="stat-value">{resellerStats.total || 0}</div>
                  <div className="stat-detail">{resellerStats.active || 0} actifs</div>
                </div>
              </div>

              <div className="stat-card reseller-commissions">
                <div className="stat-icon">💸</div>
                <div className="stat-content">
                  <h3>Commissions Totales</h3>
                  <div className="stat-value">{formatCurrency(resellerStats.totalCommissions)}</div>
                </div>
              </div>

              <div className="stat-card reseller-withdrawals">
                <div className="stat-icon">🏦</div>
                <div className="stat-content">
                  <h3>Retraits Effectués</h3>
                  <div className="stat-value">{formatCurrency(resellerStats.totalWithdrawals)}</div>
                </div>
              </div>

              <div className="stat-card reseller-pending">
                <div className="stat-icon">⏳</div>
                <div className="stat-content">
                  <h3>Solde Disponible</h3>
                  <div className="stat-value">{formatCurrency(resellerStats.pendingBalance)}</div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Appareils */}
        {deviceStats && (
          <section className="stats-section">
            <h2>📱 Statistiques Appareils</h2>
            <div className="stats-grid">
              <div className="stat-card device-users">
                <div className="stat-icon">👤</div>
                <div className="stat-content">
                  <h3>Utilisateurs avec Appareils</h3>
                  <div className="stat-value">{deviceStats.stats?.users_with_devices || 0}</div>
                </div>
              </div>

              <div className="stat-card device-total">
                <div className="stat-icon">📲</div>
                <div className="stat-content">
                  <h3>Total Appareils</h3>
                  <div className="stat-value">{deviceStats.stats?.total_devices || 0}</div>
                </div>
              </div>

              <div className="stat-card device-avg">
                <div className="stat-icon">📊</div>
                <div className="stat-content">
                  <h3>Moy. Connexions</h3>
                  <div className="stat-value">
                    {deviceStats.stats?.avg_connections_per_device 
                      ? parseFloat(deviceStats.stats.avg_connections_per_device).toFixed(1)
                      : '0'}
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Nouveaux Utilisateurs */}
        {stats?.newUsers && stats.newUsers.length > 0 && (
          <section className="stats-section">
            <h2>🆕 Nouveaux Utilisateurs (7 derniers jours)</h2>
            <div className="users-timeline">
              {stats.newUsers.map((day, index) => (
                <div key={index} className="timeline-item">
                  <div className="timeline-date">
                    {new Date(day.date).toLocaleDateString('fr-FR', { 
                      weekday: 'short', 
                      day: 'numeric', 
                      month: 'short' 
                    })}
                  </div>
                  <div className="timeline-count">{day.count} nouveau(x)</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Revenus par Mois */}
        {stats?.revenueByMonth && stats.revenueByMonth.length > 0 && (
          <section className="stats-section">
            <h2>📈 Revenus par Mois (6 derniers mois)</h2>
            <div className="revenue-chart">
              {stats.revenueByMonth.map((month, index) => (
                <div key={index} className="chart-bar">
                  <div className="bar-value">{formatCurrency(month.total)}</div>
                  <div 
                    className="bar-fill" 
                    style={{
                      height: `${(month.total / Math.max(...stats.revenueByMonth.map(m => m.total))) * 100}%`
                    }}
                  ></div>
                  <div className="bar-label">
                    {new Date(month.month + '-01').toLocaleDateString('fr-FR', { 
                      month: 'short', 
                      year: 'numeric' 
                    })}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminHome;
